import { Button } from 'antd'
import { UserCog } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  exitImpersonation,
  getImpersonationLabel,
  IMPERSONATION_BANNER_HEIGHT,
  isImpersonating
} from 'src/utils/impersonation'

/**
 * Persistent banner shown while an admin is impersonating a member. Reading
 * localStorage at render is fine: impersonation start/exit both perform a full
 * page reload, so the value is stable for the lifetime of the page.
 *
 * It is `sticky` (not `fixed`) so it occupies real layout space above the app
 * shell — otherwise it overlapped and hid the topbar. While mounted it also
 * publishes its height as `--impersonation-offset`, which the layout shell uses
 * to drop the sticky sidebar (and its `100vh`) below the banner.
 */
export const ImpersonationBanner = () => {
  const { t } = useTranslation()
  const show = isImpersonating()

  useEffect(() => {
    if (!show) return
    const root = document.documentElement
    root.style.setProperty(
      '--impersonation-offset',
      `${IMPERSONATION_BANNER_HEIGHT}px`
    )
    return () => {
      root.style.removeProperty('--impersonation-offset')
    }
  }, [show])

  if (!show) return null

  return (
    <div
      role="status"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2000,
        height: IMPERSONATION_BANNER_HEIGHT,
        boxSizing: 'border-box',
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: '6px 16px',
        background: 'var(--ant-color-warning)',
        color: 'var(--ant-color-text-light-solid, #fff)',
        fontSize: 13,
        fontWeight: 500,
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
      }}
    >
      <UserCog size={15} />
      <span>
        {t('users.lifecycle.impersonate.banner', {
          name: getImpersonationLabel()
        })}
      </span>
      <Button size="small" onClick={() => exitImpersonation()}>
        {t('users.lifecycle.impersonate.exit')}
      </Button>
    </div>
  )
}
